import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Save, Plus, X, CheckCircle2, AlertCircle, Building2, Mail, Phone, Globe } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || "http://localhost:8001") + "/api";

const AdminCompanyProfile = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const [profileExists, setProfileExists] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  // Form state
  const [formData, setFormData] = useState({
    company_name: '',
    email: '',
    phone: '',
    website: '',
    description: '',
    services_offered: [],
    products_offered: [],
    industries_served: [],
    geographies_focus: [],
    unique_value_props: [],
    case_highlights: [],
    compliance_badges: [],
    brand_tone: 'Professional, concise, outcome-focused'
  });
  
  // Input fields for arrays
  const [serviceInput, setServiceInput] = useState('');
  const [productInput, setProductInput] = useState('');
  const [industryInput, setIndustryInput] = useState('');
  const [geoInput, setGeoInput] = useState('');
  const [uvpInput, setUvpInput] = useState('');
  const [badgeInput, setBadgeInput] = useState('');
  
  // Case highlight inputs
  const [caseTitle, setCaseTitle] = useState('');
  const [caseResult, setCaseResult] = useState('');
  const [caseLink, setCaseLink] = useState('');
  
  const [metadata, setMetadata] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  // Autosave effect - debounced save when formData changes
  useEffect(() => {
    // Skip on initial load or when manually saving
    if (isInitialLoad || saving) {
      return;
    }

    // Skip autosave if required fields are missing
    if (!formData.company_name || !formData.email || !formData.description) {
      return;
    }

    // Debounce autosave by 2 seconds
    const autoSaveTimer = setTimeout(() => {
      handleAutoSave();
    }, 2000);

    return () => clearTimeout(autoSaveTimer);
  }, [formData, isInitialLoad, saving]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/admin/company-profile`, {
        withCredentials: true
      });
      
      if (response.data.exists) {
        setFormData(response.data.profile);
        setProfileExists(true);
        setMetadata({
          updated_at: response.data.profile.updated_at,
          updated_by: response.data.profile.updated_by,
          created_at: response.data.profile.created_at
        });
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error("Failed to load company profile");
    } finally {
      setLoading(false);
      // Mark initial load complete after a short delay
      setTimeout(() => setIsInitialLoad(false), 500);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const addToArray = (arrayName, value, clearInput) => {
    if (!value.trim()) return;
    
    setFormData(prev => ({
      ...prev,
      [arrayName]: [...(prev[arrayName] || []), value.trim()]
    }));
    clearInput('');
  };

  const removeFromArray = (arrayName, index) => {
    setFormData(prev => ({
      ...prev,
      [arrayName]: prev[arrayName].filter((_, i) => i !== index)
    }));
  };

  const addCaseHighlight = () => {
    if (!caseTitle.trim() || !caseResult.trim()) return;
    
    setFormData(prev => ({
      ...prev,
      case_highlights: [
        ...prev.case_highlights,
        {
          title: caseTitle.trim(),
          result: caseResult.trim(),
          link: caseLink.trim() || null
        }
      ]
    }));
    
    setCaseTitle('');
    setCaseResult('');
    setCaseLink('');
  };

  const removeCaseHighlight = (index) => {
    setFormData(prev => ({
      ...prev,
      case_highlights: prev.case_highlights.filter((_, i) => i !== index)
    }));
  };

  const handleAutoSave = async () => {
    setAutoSaving(true);
    try {
      const response = await axios.put(
        `${API}/admin/company-profile`,
        formData,
        { withCredentials: true }
      );

      setProfileExists(true);
      setMetadata({
        updated_at: response.data.profile.updated_at,
        updated_by: response.data.profile.updated_by,
        created_at: response.data.profile.created_at
      });

      // Silent autosave - no toast notification
    } catch (error) {
      console.error('Error autosaving profile:', error);
      // Only show error toast for autosave failures
      toast.error("Changes could not be saved automatically");
    } finally {
      setAutoSaving(false);
    }
  };

  const handleSave = async () => {
    // Validation
    if (!formData.company_name || !formData.email || !formData.description) {
      toast.error("Company name, email, and description are required");
      return;
    }

    setSaving(true);
    try {
      const response = await axios.put(
        `${API}/admin/company-profile`,
        formData,
        { withCredentials: true }
      );

      setProfileExists(true);
      setMetadata({
        updated_at: response.data.profile.updated_at,
        updated_by: response.data.profile.updated_by,
        created_at: response.data.profile.created_at
      });

      toast.success(response.data.message || "Company profile saved successfully!");
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error(error.response?.data?.detail || "Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-6 h-6 text-indigo-600" />
                Admin Company Profile
              </CardTitle>
              <CardDescription>
                Configure your company profile to personalize lead insights and email generation
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {autoSaving && (
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600 mr-1"></div>
                  Autosaving...
                </Badge>
              )}
              {profileExists && !autoSaving && (
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  Saved
                </Badge>
              )}
            </div>
          </div>
          
          {metadata && (
            <div className="text-xs text-slate-500 mt-2">
              Last updated: {new Date(metadata.updated_at).toLocaleString()} by {metadata.updated_by}
            </div>
          )}
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Core Identity */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-slate-900 border-b pb-2">Core Identity (Required)</h3>
            
            <div>
              <Label htmlFor="company_name">Company Name *</Label>
              <Input
                id="company_name"
                name="company_name"
                value={formData.company_name}
                onChange={handleInputChange}
                placeholder="e.g., Regulatory Solutions Inc"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="email">Company Email *</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="contact@company.com"
                  className="mt-1 pl-10"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="description">Company Description * (1-400 chars)</Label>
              <Textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Brief description of your company and what you do..."
                maxLength={400}
                rows={3}
                className="mt-1"
              />
              <div className="text-xs text-slate-500 mt-1">
                {formData.description.length}/400 characters
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-slate-900 border-b pb-2">Contact Information (Optional)</h3>
            
            <div>
              <Label htmlFor="phone">Phone Number</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                <Input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+1-555-123-4567"
                  className="mt-1 pl-10"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="website">Website</Label>
              <div className="relative">
                <Globe className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                <Input
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleInputChange}
                  placeholder="https://company.com"
                  className="mt-1 pl-10"
                />
              </div>
            </div>
          </div>

          {/* Services & Products */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-slate-900 border-b pb-2">What You Offer</h3>
            
            <div>
              <Label>Services Offered</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={serviceInput}
                  onChange={(e) => setServiceInput(e.target.value)}
                  placeholder="e.g., Regulatory Dossier Preparation"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addToArray('services_offered', serviceInput, setServiceInput))}
                />
                <Button
                  type="button"
                  onClick={() => addToArray('services_offered', serviceInput, setServiceInput)}
                  size="sm"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.services_offered.map((service, idx) => (
                  <Badge key={idx} variant="secondary" className="flex items-center gap-1">
                    {service}
                    <X className="w-3 h-3 cursor-pointer" onClick={() => removeFromArray('services_offered', idx)} />
                  </Badge>
                ))}
              </div>
            </div>

            <div>
              <Label>Products Offered</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={productInput}
                  onChange={(e) => setProductInput(e.target.value)}
                  placeholder="e.g., Compliance Dashboard"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addToArray('products_offered', productInput, setProductInput))}
                />
                <Button
                  type="button"
                  onClick={() => addToArray('products_offered', productInput, setProductInput)}
                  size="sm"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.products_offered.map((product, idx) => (
                  <Badge key={idx} variant="secondary" className="flex items-center gap-1">
                    {product}
                    <X className="w-3 h-3 cursor-pointer" onClick={() => removeFromArray('products_offered', idx)} />
                  </Badge>
                ))}
              </div>
            </div>
          </div>

          {/* Target Markets */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-slate-900 border-b pb-2">Who You Serve</h3>
            
            <div>
              <Label>Industries Served</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={industryInput}
                  onChange={(e) => setIndustryInput(e.target.value)}
                  placeholder="e.g., Pharmaceutical"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addToArray('industries_served', industryInput, setIndustryInput))}
                />
                <Button
                  type="button"
                  onClick={() => addToArray('industries_served', industryInput, setIndustryInput)}
                  size="sm"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.industries_served.map((industry, idx) => (
                  <Badge key={idx} variant="outline" className="flex items-center gap-1">
                    {industry}
                    <X className="w-3 h-3 cursor-pointer" onClick={() => removeFromArray('industries_served', idx)} />
                  </Badge>
                ))}
              </div>
            </div>

            <div>
              <Label>Geographic Focus</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={geoInput}
                  onChange={(e) => setGeoInput(e.target.value)}
                  placeholder="e.g., India, MEA, EU"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addToArray('geographies_focus', geoInput, setGeoInput))}
                />
                <Button
                  type="button"
                  onClick={() => addToArray('geographies_focus', geoInput, setGeoInput)}
                  size="sm"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.geographies_focus.map((geo, idx) => (
                  <Badge key={idx} variant="outline" className="flex items-center gap-1">
                    {geo}
                    <X className="w-3 h-3 cursor-pointer" onClick={() => removeFromArray('geographies_focus', idx)} />
                  </Badge>
                ))}
              </div>
            </div>
          </div>

          {/* Differentiation */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-slate-900 border-b pb-2">Differentiation</h3>
            
            <div>
              <Label>Unique Value Propositions</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={uvpInput}
                  onChange={(e) => setUvpInput(e.target.value)}
                  placeholder="e.g., 98% first-time approval rate"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addToArray('unique_value_props', uvpInput, setUvpInput))}
                />
                <Button
                  type="button"
                  onClick={() => addToArray('unique_value_props', uvpInput, setUvpInput)}
                  size="sm"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.unique_value_props.map((uvp, idx) => (
                  <Badge key={idx} className="bg-indigo-100 text-indigo-700 border-indigo-200 flex items-center gap-1">
                    {uvp}
                    <X className="w-3 h-3 cursor-pointer" onClick={() => removeFromArray('unique_value_props', idx)} />
                  </Badge>
                ))}
              </div>
            </div>

            <div>
              <Label>Compliance Badges/Certifications</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={badgeInput}
                  onChange={(e) => setBadgeInput(e.target.value)}
                  placeholder="e.g., ISO 9001, GxP Certified"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addToArray('compliance_badges', badgeInput, setBadgeInput))}
                />
                <Button
                  type="button"
                  onClick={() => addToArray('compliance_badges', badgeInput, setBadgeInput)}
                  size="sm"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.compliance_badges.map((badge, idx) => (
                  <Badge key={idx} className="bg-green-100 text-green-700 border-green-200 flex items-center gap-1">
                    {badge}
                    <X className="w-3 h-3 cursor-pointer" onClick={() => removeFromArray('compliance_badges', idx)} />
                  </Badge>
                ))}
              </div>
            </div>

            <div>
              <Label>Case Highlights / Success Stories</Label>
              <div className="space-y-2 mt-2 p-4 bg-slate-50 rounded-lg">
                <Input
                  value={caseTitle}
                  onChange={(e) => setCaseTitle(e.target.value)}
                  placeholder="Title (e.g., Accelerated EU Market Entry)"
                  className="bg-white"
                />
                <Textarea
                  value={caseResult}
                  onChange={(e) => setCaseResult(e.target.value)}
                  placeholder="Result/Outcome (e.g., Achieved approval 4 months ahead, saving $2M)"
                  rows={2}
                  className="bg-white"
                />
                <Input
                  value={caseLink}
                  onChange={(e) => setCaseLink(e.target.value)}
                  placeholder="Link (optional)"
                  className="bg-white"
                />
                <Button
                  type="button"
                  onClick={addCaseHighlight}
                  size="sm"
                  disabled={!caseTitle.trim() || !caseResult.trim()}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Case Study
                </Button>
              </div>
              
              {formData.case_highlights.length > 0 && (
                <div className="space-y-2 mt-2">
                  {formData.case_highlights.map((caseStudy, idx) => (
                    <div key={idx} className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-sm text-blue-900">{caseStudy.title}</p>
                          <p className="text-sm text-blue-700 mt-1">{caseStudy.result}</p>
                          {caseStudy.link && (
                            <a href={caseStudy.link} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline mt-1 block">
                              View case study →
                            </a>
                          )}
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeCaseHighlight(idx)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Brand Voice */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-slate-900 border-b pb-2">Brand Voice</h3>
            
            <div>
              <Label htmlFor="brand_tone">Communication Tone & Style</Label>
              <Input
                id="brand_tone"
                name="brand_tone"
                value={formData.brand_tone}
                onChange={handleInputChange}
                placeholder="e.g., Professional, authoritative, outcome-driven"
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">
                This guides how insights and emails are generated
              </p>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex items-center gap-3 pt-4 border-t">
            <Button
              onClick={handleSave}
              disabled={saving || autoSaving}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Now
                </>
              )}
            </Button>
            
            {autoSaving && (
              <div className="flex items-center gap-2 text-blue-600 text-sm">
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
                <span>Autosaving changes...</span>
              </div>
            )}
            
            {!autoSaving && (!formData.company_name || !formData.email || !formData.description) ? (
              <div className="flex items-center gap-2 text-amber-600 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>Required fields missing (autosave disabled)</span>
              </div>
            ) : !autoSaving && (
              <div className="flex items-center gap-2 text-slate-500 text-sm">
                <CheckCircle2 className="w-4 h-4" />
                <span>Changes autosave after 2 seconds</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminCompanyProfile;
